import {Button} from 'antd';
import React, {
    memo,
    ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import {Link} from 'react-router-dom';

import {IMenuNavLink} from 'components/layout/layout-types';
import {REQUESTS_ENTITY_NAME} from 'components/request/constants/requests.constants';
import {selectContextRawData} from 'modules/context/context-selector';
import {selectFormEntityData} from 'modules/data/data-selectors';
import {ReactComponent as ExitIcon} from 'shared/assets/header/exit.svg';
import {ReactComponent as FileUpload} from 'shared/assets/header/file-upload.svg';
import {ReactComponent as NotificationActive} from 'shared/assets/header/notification-active.svg';
import {ReactComponent as Notification} from 'shared/assets/header/notification.svg';
import {ReactComponent as RequestsIcon} from 'shared/assets/header/requests-icon.svg';
import {URL_LOGIN} from 'shared/constants/urls';
import {useAppHistory} from 'shared/hooks/use-app-history';
import {useDebouncedEffect} from 'shared/hooks/use-debounced-effect';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {
    logout,
    selectUserId,
    selectUserInfo,
} from 'store/slices/auth-slice';
import {selectThunkStatus} from 'store/slices/loading-state-slice';
import {selectMenuStructure} from 'store/slices/menu-slice';
import {readRequest} from 'store/slices/request-slice/request-slice-thunks';
import {useWebsocketSubscriber} from 'websockets';

import {DynamicIcon, TIconType} from '../../dynamic-icon';
import {NetDirectoryFileUploaderModal} from '../../net-directory-file-uploader-modal/net-directory-file-uploader-modal';
import {fetchHasUnreadRequests, loadListOfNews} from '../api/api';
import {getUsernameLetterALias, hasMenuItemNewsSection} from '../lib';
import {NEWS_ENTITY_NAME} from '../model/constants';
import {INewsRef} from '../model/types';
import s from './header.module.less';

interface IHeaderProps {
    children?: ReactNode;
    menuContent?: ReactNode;
    menuNavLinks?: IMenuNavLink[];
}

const HeaderComponent = ({children, menuContent, menuNavLinks = []}: IHeaderProps) => {
    const dispatch = useAppDispatch();
    const history = useAppHistory();

    const [needRead, setNeedRead] = useState(false);
    const [hasUnreadRequests, setHasUnreadRequests] = useState(false);

    const newsRef = useRef<INewsRef>({
        needRead: false,
        prevState: false,
        firstRender: true,
    });

    const menuStructure = useAppSelector(selectMenuStructure);
    const newsData = useAppSelector(selectFormEntityData(NEWS_ENTITY_NAME));
    const userId = useAppSelector(selectUserId);
    const userInfo = useAppSelector(selectUserInfo);
    const userName = userInfo?.fullName || '';
    const {organizationId, taxPeriodId} = useAppSelector(selectContextRawData) ?? {};

    const hasNewsSection = hasMenuItemNewsSection(menuStructure);
    const userNameAlias = getUsernameLetterALias(userName);

    const {isFulfilled: isRequestsRead} = useAppSelector(st => selectThunkStatus(st, readRequest.typePrefix));

    useDebouncedEffect(() => {
        const fetchRequests = async () => {
            try {
                const {data} = await fetchHasUnreadRequests();
                setHasUnreadRequests(data);
            } catch (error) {
                console.error('Ошибка при получении информации о непрочитанных запросах: ', error);
            }
        };

        fetchRequests();
    }, [isRequestsRead, organizationId, taxPeriodId], 300);

    useEffect(() => {
        if (userId) {
            loadListOfNews({
                userId,
                newsRef: newsRef.current,
                setNeedRead,
                shouldShowErrorToUser: true,
            });
        }
    }, [newsData]);

    useWebsocketSubscriber({
        newsAdded: msg => {
            if (msg.data?.newsAdded) {
                setNeedRead(true);
            }
        },
    }, {
        fallbacks: [{
            execute: () => {
                if (userId) {
                    loadListOfNews({
                        userId,
                        newsRef: newsRef.current,
                        setNeedRead,
                    });
                }
            },
            interval: 30000,
        }],
    });

    const handleLogout = useCallback(() => {
        dispatch(logout())
            .unwrap()
            .then(() => history.push(URL_LOGIN));
    }, [dispatch, history]);

    const renderNavLinks = () => (
        menuNavLinks.map(linkItem => (
            <a
                href={linkItem.url}
                className={s.header__action}
                target="_blank"
                rel="noopener noreferrer"
                key={linkItem.url}
            >
                <DynamicIcon
                    type={linkItem.icon as TIconType}
                    className={s.header__icon}
                />
            </a>
        ))
    );

    return (
        <header className={s.header}>
            {children}

            <div className={s.header__menu}>
                <div className={s.header__actions}>
                    {renderNavLinks()}
                    {menuContent}

                    <NetDirectoryFileUploaderModal
                        className={s.header__action}
                        icon={<FileUpload className={s.header__icon} />}
                    />

                    {hasNewsSection && (
                        <Link
                            to={`/${NEWS_ENTITY_NAME}`}
                            className={s.header__action}
                        >
                            {needRead ? (
                                <NotificationActive className={s.header__icon} />
                            ) : <Notification className={s.header__icon} />}
                        </Link>
                    )}
                </div>

                <Link
                    to={`/${REQUESTS_ENTITY_NAME}`}
                    className={s['header__requests-button']}
                >
                    <div className={s['requests-button__icon-wrapper']}>
                        <RequestsIcon className={s['requests-button__icon']} />
                        {hasUnreadRequests && (
                            <span className={s['requests-button__unread-badge']} />
                        )}
                    </div>

                    <span>Запросы</span>
                </Link>

                <Link
                    to="/settings.user.settings/settings.user.settings.general"
                    className={s.header__profile}
                >
                    <div className={s['header__profile-avatar']}>
                        {userNameAlias}
                    </div>
                </Link>

                <Button
                    onClick={handleLogout}
                    className={s['header__exit-button']}
                    icon={<ExitIcon />}
                    type="link"
                />
            </div>
        </header>
    );
};

export const Header = memo(HeaderComponent);
